/**
 * Schema visual registry — populated by defineModule().
 *
 * No hardcoded data. Each module registers its entity types
 * with icon/label via defineModule({ entityLabels: { ... } }).
 * moduleId is derived from schema prefix (before first dot).
 */

import type { IconName } from "../../components/ui/Icon";

export interface SchemaVisual {
  readonly icon: IconName;
  readonly label: string;
  readonly tabLabel?: string;
  readonly moduleId: string;
  readonly themeColor?: string;
}

interface SchemaEntry {
  readonly icon: IconName;
  readonly label: string;
  readonly tabLabel?: string;
  readonly themeColor?: string;
}

// ─── Runtime registry — populated by registerSchemaVisuals() ────────

const registry = new Map<string, SchemaEntry>();

/** Register schema visuals. Called by defineModule(). */
export function registerSchemaVisuals(
  entries: readonly { schemaId: string; entry: SchemaEntry }[],
): void {
  for (const { schemaId, entry } of entries) {
    registry.set(schemaId, entry);
  }
}

/** Derive moduleId from schema_id: "telegram.chat" → "telegram" */
function deriveModuleId(schemaId: string): string {
  const dotIdx = schemaId.indexOf(".");
  return dotIdx >= 0 ? schemaId.slice(0, dotIdx) : schemaId;
}

// Schema visuals for modules without a frontend UI module
registerSchemaVisuals([
  { schemaId: "web.link", entry: { icon: "link", label: "Link", tabLabel: "Links" } },
  { schemaId: "web.page", entry: { icon: "globe", label: "Page", tabLabel: "Pages" } },
  { schemaId: "web.bookmark", entry: { icon: "link", label: "Bookmark", tabLabel: "Bookmarks" } },
]);

const FALLBACK: SchemaEntry = { icon: "file", label: "Entity", tabLabel: "Entities" };

export function schemaIcon(schemaId: string): IconName {
  return (registry.get(schemaId) ?? FALLBACK).icon;
}

export function schemaLabel(schemaId: string): string {
  return (registry.get(schemaId) ?? FALLBACK).label;
}

export function schemaTabLabel(schemaId: string): string {
  const entry = registry.get(schemaId) ?? FALLBACK;
  return entry.tabLabel ?? entry.label + "s";
}

export function schemaVisual(schemaId: string): SchemaVisual {
  const entry = registry.get(schemaId) ?? FALLBACK;
  return {
    ...entry,
    moduleId: deriveModuleId(schemaId),
    themeColor: entry.themeColor,
  };
}

/** All registered schema entries — for mention popup categories */
export function allSchemaEntries(): readonly { readonly icon: IconName; readonly label: string; readonly schemaId: string }[] {
  return Array.from(registry.entries()).map(([schemaId, entry]) => ({
    icon: entry.icon,
    label: entry.tabLabel ?? entry.label + "s",
    schemaId,
  }));
}
